import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    IconButton,
    Alert,
    Button,
    CircularProgress,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Tooltip
} from '@mui/material';
import {
    Close as CloseIcon,
    History as HistoryIcon,
    FileDownload as FileDownloadIcon,
    Refresh as RefreshIcon,
    Campaign as CampaignIcon,
    People as PeopleIcon,
    Schedule as ScheduleIcon,
    FolderOpen as FolderOpenIcon,
    Description as DescriptionIcon
} from '@mui/icons-material';
import { useListExportFiles } from '../../../../client/hooks/products/promotions.queries';
import { useDownloadExport } from '../../../../client/hooks/products/promotions.mutations';

const formatNumber = (value) => (value == null ? '-' : Number(value).toLocaleString('pt-BR'));

const formatFileSize = (bytes) => {
    if (bytes == null) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toLocaleString('pt-BR', { maximumFractionDigits: 0 })} KB`;
    return `${(bytes / (1024 * 1024)).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} MB`;
};

const formatDate = (value) => (value ? new Date(value).toLocaleDateString('pt-BR') : '-');

const formatTime = (value) => (value ? new Date(value).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '');

const formatRelative = (value) => {
    if (!value) return '-';
    const minutes = Math.floor((Date.now() - new Date(value).getTime()) / 60000);
    if (minutes < 1) return 'Agora mesmo';
    if (minutes < 60) return `Há ${minutes} min`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `Há ${hours}h`;
    const days = Math.floor(hours / 24);
    return days === 1 ? 'Ontem' : `Há ${days} dias`;
};

const average = (file) => {
    if (!file.totalCampaigns || file.totalCandidates == null) return null;
    return Math.round(file.totalCandidates / file.totalCampaigns);
};

const headCellSx = {
    fontWeight: 600,
    fontSize: '0.8rem',
    color: '#616161',
    bgcolor: '#fafafa',
    borderBottom: '1px solid #eee',
    whiteSpace: 'nowrap',
    py: 1.5
};

const numberCellSx = {
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap'
};

function StatBlock({ icon: Icon, color, title, value, caption, loading }) {
    return (
        <Box sx={{
            flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 0' },
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            p: 2,
            border: '1px solid #eee',
            borderRadius: 2,
            bgcolor: 'white'
        }}>
            <Box sx={{
                p: 1.25,
                borderRadius: 2,
                bgcolor: `${color}15`,
                display: 'flex'
            }}>
                <Icon sx={{ color, fontSize: 26 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    {title}
                </Typography>
                {loading ? (
                    <Skeleton width={70} height={30} />
                ) : (
                    <Typography sx={{ fontSize: '1.35rem', fontWeight: 700, color: '#1a1a1a', lineHeight: 1.3, fontVariantNumeric: 'tabular-nums' }}>
                        {value}
                    </Typography>
                )}
                {caption && !loading && (
                    <Typography variant="caption" color="text.secondary">
                        {caption}
                    </Typography>
                )}
            </Box>
        </Box>
    );
}

function Variation({ current, previous }) {
    if (current == null || previous == null) return null;
    const diff = current - previous;
    if (diff === 0) {
        return <Typography variant="caption" color="text.secondary">sem variação</Typography>;
    }
    return (
        <Tooltip title={`Em relação à exportação anterior (${formatNumber(previous)})`} arrow>
            <Typography variant="caption" sx={{ color: diff > 0 ? '#2e7d32' : '#d32f2f', fontWeight: 600 }}>
                {diff > 0 ? '+' : '−'}{formatNumber(Math.abs(diff))}
            </Typography>
        </Tooltip>
    );
}

function LoadingRows({ count }) {
    return Array.from({ length: count }).map((_, index) => (
        <TableRow key={index}>
            <TableCell sx={{ pl: 3 }}>
                <Skeleton width={90} />
                <Skeleton width={110} height={16} />
            </TableCell>
            <TableCell><Skeleton width={260} /></TableCell>
            <TableCell align="right"><Skeleton width={30} sx={{ ml: 'auto' }} /></TableCell>
            <TableCell align="right"><Skeleton width={60} sx={{ ml: 'auto' }} /></TableCell>
            <TableCell align="right"><Skeleton width={40} sx={{ ml: 'auto' }} /></TableCell>
            <TableCell align="right"><Skeleton width={50} sx={{ ml: 'auto' }} /></TableCell>
            <TableCell align="center" sx={{ pr: 3 }}><Skeleton variant="rounded" width={90} height={32} sx={{ mx: 'auto' }} /></TableCell>
        </TableRow>
    ));
}

export default function ExportHistoryModal({ open, onClose, onSuccess, onError }) {
    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(10);
    const [downloading, setDownloading] = useState({});

    const { data, isLoading, isFetching, error, refetch } = useListExportFiles({ page, limit }, open);
    const { data: latestData, isLoading: latestLoading, refetch: refetchLatest } = useListExportFiles({ page: 0, limit: 1 }, open);
    const downloadMutation = useDownloadExport();

    const files = data?.files || [];
    const total = data?.total || 0;
    const latest = latestData?.files?.[0];

    const handleRefresh = () => {
        refetch();
        refetchLatest();
    };

    const handleDownload = async (file) => {
        setDownloading(prev => ({ ...prev, [file.jobId]: true }));
        try {
            const fileBlob = await downloadMutation.mutateAsync({ jobId: file.jobId });

            const url = window.URL.createObjectURL(fileBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = file.fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            onSuccess?.("Download concluído!");
        } catch (err) {
            onError?.("Erro ao baixar o arquivo. A exportação pode ter expirado.");
        } finally {
            setDownloading(prev => {
                const { [file.jobId]: _, ...rest } = prev;
                return rest;
            });
        }
    };

    const handleLimitChange = (event) => {
        setLimit(parseInt(event.target.value, 10));
        setPage(0);
    };

    const renderTableBody = () => {
        if (isLoading) return <LoadingRows count={5} />;

        return files.map((file, index) => {
            const previous = files[index + 1];
            const isDownloading = !!downloading[file.jobId];
            const avg = average(file);

            return (
                <TableRow key={file.jobId} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                    <TableCell sx={{ pl: 3, py: 1.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1a1a1a', whiteSpace: 'nowrap' }}>
                            {formatDate(file.exportDate)}
                            <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400, ml: 0.75 }}>
                                {formatTime(file.exportDate)}
                            </Box>
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {formatRelative(file.exportDate)}
                        </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 320 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                            <DescriptionIcon sx={{ fontSize: 18, color: '#4caf50', flexShrink: 0 }} />
                            <Tooltip title={file.fileName} arrow placement="top-start">
                                <Typography
                                    variant="body2"
                                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#424242' }}
                                >
                                    {file.fileName}
                                </Typography>
                            </Tooltip>
                        </Box>
                    </TableCell>
                    <TableCell align="right" sx={numberCellSx}>
                        <Typography variant="body2">{formatNumber(file.totalCampaigns)}</Typography>
                        {previous && <Variation current={file.totalCampaigns} previous={previous.totalCampaigns} />}
                    </TableCell>
                    <TableCell align="right" sx={numberCellSx}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatNumber(file.totalCandidates)}</Typography>
                        {previous && <Variation current={file.totalCandidates} previous={previous.totalCandidates} />}
                    </TableCell>
                    <TableCell align="right" sx={{ ...numberCellSx, color: 'text.secondary' }}>
                        {formatNumber(avg)}
                    </TableCell>
                    <TableCell align="right" sx={{ ...numberCellSx, color: 'text.secondary' }}>
                        {formatFileSize(file.fileSize)}
                    </TableCell>
                    <TableCell align="center" sx={{ pr: 3 }}>
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleDownload(file)}
                            disabled={isDownloading}
                            startIcon={isDownloading
                                ? <CircularProgress size={14} sx={{ color: '#4caf50' }} />
                                : <FileDownloadIcon fontSize="small" />}
                            sx={{
                                minWidth: 104,
                                textTransform: 'none',
                                fontWeight: 600,
                                borderRadius: 2,
                                borderColor: '#4caf50',
                                color: '#4caf50',
                                '&:hover': { borderColor: '#388e3c', bgcolor: 'rgba(76, 175, 80, 0.08)' },
                                '&.Mui-disabled': { borderColor: '#c8e6c9', color: '#81c784' }
                            }}
                        >
                            {isDownloading ? 'Baixando' : 'Baixar'}
                        </Button>
                    </TableCell>
                </TableRow>
            );
        });
    };

    const renderList = () => {
        if (error) {
            return (
                <Box sx={{ p: 3 }}>
                    <Alert
                        severity="error"
                        action={<Button color="inherit" size="small" onClick={handleRefresh}>Tentar novamente</Button>}
                    >
                        Não foi possível carregar as exportações.
                    </Alert>
                </Box>
            );
        }

        if (!isLoading && files.length === 0) {
            return (
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, px: 3, textAlign: 'center' }}>
                    <Box sx={{ p: 2, borderRadius: '50%', bgcolor: '#fff3e0', display: 'flex', mb: 2 }}>
                        <FolderOpenIcon sx={{ fontSize: 36, color: '#ff9800' }} />
                    </Box>
                    <Typography sx={{ fontWeight: 600, mb: 0.5 }}>Nenhuma exportação encontrada</Typography>
                    <Typography variant="body2" color="text.secondary">
                        As planilhas geradas em "Exportar Campanhas" aparecem aqui.
                    </Typography>
                </Box>
            );
        }

        return (
            <TableContainer sx={{ flex: 1, opacity: isFetching && !isLoading ? 0.6 : 1, transition: 'opacity 0.15s' }}>
                <Table stickyHeader size="small" sx={{ minWidth: 900 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ ...headCellSx, pl: 3 }}>Data</TableCell>
                            <TableCell sx={headCellSx}>Arquivo</TableCell>
                            <TableCell sx={headCellSx} align="right">Campanhas</TableCell>
                            <TableCell sx={headCellSx} align="right">Candidatos</TableCell>
                            <TableCell sx={headCellSx} align="right">
                                <Tooltip title="Média de candidatos por campanha" arrow>
                                    <span>Média/campanha</span>
                                </Tooltip>
                            </TableCell>
                            <TableCell sx={headCellSx} align="right">Tamanho</TableCell>
                            <TableCell sx={{ ...headCellSx, pr: 3 }} align="center">Ação</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {renderTableBody()}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                    height: '85vh',
                    maxHeight: '85vh'
                }
            }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 3, py: 2.5 }}>
                <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: '#fff3e0', display: 'flex' }}>
                    <HistoryIcon sx={{ color: '#ff9800', fontSize: 28 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 700, color: '#1a1a1a', lineHeight: 1.3 }}>
                        Histórico de Exportações
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Planilhas de candidatos geradas em "Exportar Campanhas"
                    </Typography>
                </Box>
                <Tooltip title="Atualizar" arrow>
                    <span>
                        <IconButton onClick={handleRefresh} disabled={isFetching}>
                            <RefreshIcon sx={{
                                color: '#ff9800',
                                animation: isFetching ? 'spin 1s linear infinite' : 'none',
                                '@keyframes spin': {
                                    '0%': { transform: 'rotate(0deg)' },
                                    '100%': { transform: 'rotate(360deg)' }
                                }
                            }} />
                        </IconButton>
                    </span>
                </Tooltip>
                <IconButton onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, px: 3, pb: 2.5 }}>
                <StatBlock
                    icon={FolderOpenIcon}
                    color="#ff9800"
                    title="Total de exportações"
                    value={formatNumber(total)}
                    loading={isLoading}
                />
                <StatBlock
                    icon={ScheduleIcon}
                    color="#757575"
                    title="Última exportação"
                    value={latest ? formatRelative(latest.exportDate) : '-'}
                    caption={latest ? `${formatDate(latest.exportDate)} às ${formatTime(latest.exportDate)}` : null}
                    loading={latestLoading}
                />
                <StatBlock
                    icon={CampaignIcon}
                    color="#ff9800"
                    title="Campanhas na última"
                    value={formatNumber(latest?.totalCampaigns)}
                    loading={latestLoading}
                />
                <StatBlock
                    icon={PeopleIcon}
                    color="#4caf50"
                    title="Candidatos na última"
                    value={formatNumber(latest?.totalCandidates)}
                    caption={latest && average(latest) != null ? `≈ ${formatNumber(average(latest))} por campanha` : null}
                    loading={latestLoading}
                />
            </Box>

            <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
                {renderList()}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 1, justifyContent: 'space-between' }}>
                <TablePagination
                    component="div"
                    count={total}
                    page={total === 0 ? 0 : page}
                    onPageChange={(event, newPage) => setPage(newPage)}
                    rowsPerPage={limit}
                    onRowsPerPageChange={handleLimitChange}
                    rowsPerPageOptions={[10, 20, 50]}
                    labelRowsPerPage="Itens por página"
                    labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
                    sx={{ '& .MuiTablePagination-toolbar': { pl: 0 } }}
                />
                <Button
                    onClick={onClose}
                    variant="outlined"
                    sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: 2,
                        borderColor: '#e0e0e0',
                        color: '#616161',
                        '&:hover': { borderColor: '#bdbdbd', bgcolor: '#fafafa' }
                    }}
                >
                    Fechar
                </Button>
            </DialogActions>
        </Dialog>
    );
}
